import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  DiagnosticBundleListQuery,
  DiagnosticBundleView,
  Partner,
  Tenant,
} from '../../api.js';
import { DataTable, type DataColumn } from '../../components/DataTable.js';
import { describeError } from '../../errors.js';
import { useFetchOnMount } from '../../hooks/useFetchOnMount.js';
import type { AdminHttpClient } from '../../http.js';
import type { ToastMessage } from '../../types.js';
import {
  MAX_DIAGNOSTIC_DOWNLOADS,
  formatDiagnosticBytes,
  formatDiagnosticDate,
  toggleDiagnosticPageSelection,
  toggleDiagnosticSelection,
} from './diagnostics-format.js';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

interface ListRequest {
  page: number;
  query: string;
  partnerId: string;
  tenantId: string;
  nonce: number;
}

export function DiagnosticsTab({
  http,
  onToast,
  active,
}: {
  http: AdminHttpClient;
  onToast: (toast: ToastMessage) => void;
  active: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<DiagnosticBundleView[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [partners, setPartners] = useState<Partner[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [listRequest, setListRequest] = useState<ListRequest>({
    page: 0,
    query: '',
    partnerId: '',
    tenantId: '',
    nonce: 0,
  });
  const searchTimer = useRef<number | undefined>(undefined);
  const translateError = useCallback(
    (key: string, options?: Record<string, unknown>) =>
      options ? String(t(key, options)) : String(t(key)),
    [t]
  );

  const load = useCallback((next: Omit<ListRequest, 'nonce'>) => {
    window.clearTimeout(searchTimer.current);
    setListRequest((current) => ({ ...next, nonce: current.nonce + 1 }));
  }, []);

  const fetchRows = useCallback(
    async (_signal: AbortSignal) => {
      const request: DiagnosticBundleListQuery = {
        limit: PAGE_SIZE,
        offset: listRequest.page * PAGE_SIZE,
        ...(listRequest.query.trim() ? { q: listRequest.query.trim() } : {}),
        ...(listRequest.partnerId ? { partnerId: listRequest.partnerId } : {}),
        ...(listRequest.tenantId ? { tenantId: listRequest.tenantId } : {}),
      };
      return http.platformDiagnosticBundles(request);
    },
    [http, listRequest]
  );

  const onData = useCallback(
    (result: Awaited<ReturnType<AdminHttpClient['platformDiagnosticBundles']>>) => {
      if (result.items.length === 0 && listRequest.page > 0) {
        load({ ...listRequest, page: Math.max(0, listRequest.page - 1) });
        return;
      }
      setRows(result.items);
      setTotal(result.total);
      setPage(listRequest.page);
    },
    [listRequest, load]
  );

  const onError = useCallback(
    (error: unknown) => onToast({ tone: 'error', message: describeError(error, translateError) }),
    [onToast, translateError]
  );
  useFetchOnMount({ enabled: active, fetcher: fetchRows, onData, onError });

  useEffect(() => {
    if (!active) return;
    void Promise.all([http.listPartners(), http.getTenants()])
      .then(([nextPartners, nextTenants]) => {
        setPartners(nextPartners);
        setTenants(nextTenants);
      })
      .catch(onError);
  }, [active, http, onError]);

  useEffect(() => () => window.clearTimeout(searchTimer.current), []);

  const visibleTenants = useMemo(
    () => tenants.filter((tenant) => !partnerId || tenant.partnerId === partnerId),
    [partnerId, tenants]
  );
  const directTenants = visibleTenants.filter((tenant) => !tenant.partnerId);
  const partnerTenants = visibleTenants.filter((tenant) => tenant.partnerId);
  const pageIds = rows.map((row) => row.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  const download = useCallback(
    async (id: string) => {
      const result = await http.platformDiagnosticBundleDownload(id);
      const anchor = document.createElement('a');
      anchor.href = result.url;
      anchor.download = `${id}.zip`;
      anchor.rel = 'noopener';
      anchor.click();
    },
    [http]
  );

  const downloadOne = useCallback(
    async (id: string) => {
      try {
        await download(id);
      } catch (error) {
        onError(error);
      }
    },
    [download, onError]
  );

  async function downloadSelected() {
    if (selected.size > MAX_DIAGNOSTIC_DOWNLOADS) {
      onToast({ tone: 'error', message: t('diagnostics.downloadLimit') });
      return;
    }
    setBusy(true);
    try {
      for (const id of selected) await download(id);
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  const columns = useMemo<DataColumn<DiagnosticBundleView>[]>(
    () => [
      {
        key: 'selected',
        ariaLabel: t('diagnostics.select'),
        header: (
          <input
            type="checkbox"
            aria-label={t('diagnostics.selectPage')}
            checked={allPageSelected}
            onChange={(event) =>
              setSelected((current) =>
                toggleDiagnosticPageSelection(current, pageIds, event.target.checked)
              )
            }
          />
        ),
        render: (row) => (
          <input
            type="checkbox"
            aria-label={t('diagnostics.selectRow', { id: row.id })}
            checked={selected.has(row.id)}
            onChange={(event) =>
              setSelected((current) =>
                toggleDiagnosticSelection(current, row.id, event.target.checked)
              )
            }
          />
        ),
      },
      {
        key: 'collectedAt',
        header: t('diagnostics.collectedAt'),
        render: (row) => formatDiagnosticDate(row.collectedAt, i18n.language),
      },
      { key: 'tenant', header: t('diagnostics.tenant'), render: (row) => row.tenantName },
      {
        key: 'user',
        header: t('diagnostics.user'),
        render: (row) => row.userName || row.userEmail || row.userId,
      },
      { key: 'device', header: t('diagnostics.device'), render: (row) => row.deviceId },
      { key: 'appVersion', header: t('diagnostics.appVersion'), render: (row) => row.appVersion },
      { key: 'platform', header: t('diagnostics.platform'), render: (row) => row.platform },
      { key: 'logCount', header: t('diagnostics.logCount'), render: (row) => row.logFileCount },
      {
        key: 'size',
        header: t('diagnostics.size'),
        render: (row) => formatDiagnosticBytes(row.sizeBytes),
      },
      {
        key: 'download',
        header: t('diagnostics.download'),
        render: (row) => (
          <button type="button" className="ghost" onClick={() => void downloadOne(row.id)}>
            {t('diagnostics.download')}
          </button>
        ),
      },
    ],
    [allPageSelected, downloadOne, i18n.language, pageIds, selected, t]
  );

  function changeFilters(nextPartnerId: string, nextTenantId: string) {
    setPartnerId(nextPartnerId);
    setTenantId(nextTenantId);
    load({ page: 0, query, partnerId: nextPartnerId, tenantId: nextTenantId });
  }

  return (
    <section className="content-stack">
      <div className="toolbar-row">
        <label>
          {t('diagnostics.partner')}
          <select value={partnerId} onChange={(event) => changeFilters(event.target.value, '')}>
            <option value="">{t('diagnostics.allPartners')}</option>
            {partners.map((partner) => (
              <option key={partner.id} value={partner.id}>
                {partner.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('tenantScope.diagnosticsFilter')}
          <select
            value={tenantId}
            onChange={(event) => changeFilters(partnerId, event.target.value)}
          >
            <option value="">{t('tenantScope.allTenants')}</option>
            {directTenants.length > 0 && (
              <optgroup label={t('platformConsole.tenantGroupDirect')}>
                {directTenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </optgroup>
            )}
            {partnerTenants.length > 0 && (
              <optgroup label={t('platformConsole.tenantGroupPartner')}>
                {partnerTenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </label>
        {selected.size > 0 && (
          <div className="toolbar-actions">
            <span>{t('diagnostics.selectedCount', { count: selected.size })}</span>
            <button type="button" className="ghost" onClick={() => setSelected(new Set())}>
              {t('diagnostics.clearSelection')}
            </button>
            <button type="button" disabled={busy} onClick={() => void downloadSelected()}>
              {t('diagnostics.downloadSelected')}
            </button>
          </div>
        )}
      </div>
      <DataTable
        rows={rows}
        columns={columns}
        empty={t('diagnostics.empty')}
        searchPlaceholder={t('diagnostics.searchPlaceholder')}
        pageSize={PAGE_SIZE}
        getRowKey={(row) => row.id}
        server={{
          total,
          page,
          onPageChange: (nextPage) => load({ page: nextPage, query, partnerId, tenantId }),
          onSearchChange: (value) => {
            setQuery(value);
            window.clearTimeout(searchTimer.current);
            searchTimer.current = window.setTimeout(
              () => load({ page: 0, query: value, partnerId, tenantId }),
              SEARCH_DEBOUNCE_MS
            );
          },
        }}
      />
    </section>
  );
}
